import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { X, Stop, CameraRotate, Image as ImageIcon, Camera, ShieldCheck, MagnifyingGlass, Trash, UploadSimple, UsersThree, UserPlus } from "@phosphor-icons/react";
import { PRESET_WALLPAPERS } from "./goLiveData";

const GUEST_MAX = 2;

export default function LiveSettingsDrawer({ open, live, onClose, onUpdate, onEnd }) {
    const [savingField, setSavingField] = useState(null);
    const [uploadingWp, setUploadingWp] = useState(false);
    const [topic, setTopic] = useState(live?.topic || "");
    const [wpUrl, setWpUrl] = useState(live?.wallpaper_url || "");
    const [useWp, setUseWp] = useState(!!live?.using_wallpaper);
    const [tab, setTab] = useState("settings"); // 'settings' | 'mods' | 'guests'

    // Moderators
    const [mods, setMods] = useState([]);
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Guests (tab=guests)
    const [guestSearch, setGuestSearch] = useState("");
    const [guestResults, setGuestResults] = useState([]);
    const [guestSearching, setGuestSearching] = useState(false);
    const [invitingId, setInvitingId] = useState(null);
    const guestList = live?.guests || [];
    const activeGuestsCount = guestList.length;

    useEffect(() => {
        if (!open) return;
        setTopic(live?.topic || "");
        setWpUrl(live?.wallpaper_url || "");
        setUseWp(!!live?.using_wallpaper);
    }, [open, live]);

    useEffect(() => {
        if (!open || tab !== "mods") return;
        api.get("/users/me/moderators").then(({ data }) => setMods(data)).catch(() => {});
    }, [open, tab]);

    useEffect(() => {
        if (!search.trim()) { setSearchResults([]); return; }
        let cancelled = false;
        setSearching(true);
        const t = setTimeout(() => {
            api.get(`/users/search?q=${encodeURIComponent(search.trim())}`)
                .then(({ data }) => { if (!cancelled) setSearchResults(data.slice(0, 6)); })
                .catch(() => {})
                .finally(() => { if (!cancelled) setSearching(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [search]);

    const patch = async (body, fieldKey) => {
        setSavingField(fieldKey);
        try {
            const { data } = await api.patch(`/live/${live.id}/settings`, body);
            onUpdate(data);
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setSavingField(null);
        }
    };

    const saveTopic = () => patch({ topic: topic.trim() }, "topic");
    const flipCamera = () => patch({ facing_mode: live?.facing_mode === "environment" ? "user" : "environment", using_wallpaper: false }, "facing");
    const turnOnCamera = () => { setUseWp(false); patch({ using_wallpaper: false }, "wp"); };
    const applyWallpaper = (url) => {
        setWpUrl(url);
        setUseWp(true);
        patch({ wallpaper_url: url, using_wallpaper: true }, "wp");
    };

    const onUploadWp = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Pick an image");
        if (f.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
        setUploadingWp(true);
        try {
            const form = new FormData();
            form.append("file", f);
            const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            applyWallpaper(url);
            toast.success("Wallpaper applied");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setUploadingWp(false);
        }
    };

    const addMod = async (u) => {
        try {
            await api.post("/users/me/moderators", { user_id: u.id });
            toast.success(`@${u.name} added as moderator`);
            setSearch("");
            setSearchResults([]);
            const { data } = await api.get("/users/me/moderators");
            setMods(data);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const removeMod = async (uid) => {
        try {
            await api.delete(`/users/me/moderators/${uid}`);
            setMods((arr) => arr.filter((m) => m.user.id !== uid));
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    // Guest search debounce
    useEffect(() => {
        if (!open || tab !== "guests") return;
        if (!guestSearch.trim()) { setGuestResults([]); return; }
        let cancelled = false;
        setGuestSearching(true);
        const t = setTimeout(() => {
            api.get(`/users/search?q=${encodeURIComponent(guestSearch.trim())}`)
                .then(({ data }) => { if (!cancelled) setGuestResults(data.slice(0, 6)); })
                .catch(() => {})
                .finally(() => { if (!cancelled) setGuestSearching(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [guestSearch, open, tab]);

    const inviteGuest = async (u) => {
        if (activeGuestsCount >= GUEST_MAX) {
            return toast.error(`Stage is full (max ${GUEST_MAX} guests)`);
        }
        setInvitingId(u.id);
        try {
            await api.post(`/live/${live.id}/guests/invite`, { user_id: u.id });
            toast.success(`Invited @${u.name} — expires in 5 min`);
            setGuestSearch("");
            setGuestResults([]);
            const { data } = await api.get(`/live/${live.id}`);
            onUpdate(data);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
        finally { setInvitingId(null); }
    };

    const removeGuest = async (uid, isOnStage) => {
        const word = isOnStage ? "Remove this guest from the stage?" : "Cancel this invite?";
        if (!window.confirm(word)) return;
        try {
            await api.delete(`/live/${live.id}/guests/${uid}`);
            const { data } = await api.get(`/live/${live.id}`);
            onUpdate(data);
            toast.success(isOnStage ? "Guest removed" : "Invite cancelled");
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    if (!open) return null;

    return (
        <div data-testid="live-settings-overlay" className="fixed inset-0 z-[65] bg-black/70" onClick={onClose}>
            <div
                data-testid="live-settings-drawer"
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-0 bottom-0 w-80 max-w-[92vw] bg-wta-surface border-l border-wta-border flex flex-col"
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-wta-border">
                    <div className="font-heading text-lg tracking-wider uppercase">Live Settings</div>
                    <button onClick={onClose} aria-label="Close" data-testid="live-settings-close" className="text-white/60 hover:text-white p-1"><X size={18} /></button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-wta-border text-[10px] sm:text-xs font-heading tracking-widest uppercase">
                    <button data-testid="tab-settings" onClick={() => setTab("settings")} className={`flex-1 py-3 ${tab === "settings" ? "text-wta-primary border-b-2 border-wta-primary" : "text-white/60"}`}>Stream</button>
                    <button data-testid="tab-mods" onClick={() => setTab("mods")} className={`flex-1 py-3 ${tab === "mods" ? "text-wta-primary border-b-2 border-wta-primary" : "text-white/60"}`}>Mods</button>
                    <button data-testid="tab-guests" onClick={() => setTab("guests")} className={`flex-1 py-3 flex items-center justify-center gap-1 ${tab === "guests" ? "text-wta-primary border-b-2 border-wta-primary" : "text-white/60"}`}>
                        Guests
                        <span className="text-[9px] tracking-wider rounded-full bg-wta-primary/20 text-wta-primary px-1.5 py-0.5">{activeGuestsCount}/{GUEST_MAX}</span>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {tab === "settings" ? (
                        <div className="p-4 space-y-5">
                            {/* Topic */}
                            <div>
                                <label className="text-xs tracking-widest text-wta-muted uppercase">Topic</label>
                                <div className="mt-1 flex gap-2">
                                    <input
                                        data-testid="ls-topic-input"
                                        value={topic}
                                        onChange={(e) => setTopic(e.target.value)}
                                        maxLength={140}
                                        className="flex-1 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-sm"
                                    />
                                    <button data-testid="ls-topic-save" onClick={saveTopic} disabled={savingField === "topic"} className="btn-primary px-3 py-2 rounded-sm text-xs">
                                        {savingField === "topic" ? "…" : "Save"}
                                    </button>
                                </div>
                            </div>

                            {/* Camera */}
                            <div>
                                <div className="text-xs tracking-widest text-wta-muted uppercase mb-2">Camera</div>
                                <div className="flex gap-2">
                                    <button
                                        data-testid="ls-flip-camera"
                                        onClick={flipCamera}
                                        disabled={savingField === "facing" || useWp}
                                        className="flex-1 btn-ghost flex items-center justify-center gap-2 py-2 rounded-sm text-xs disabled:opacity-40"
                                    >
                                        <CameraRotate size={14} weight="bold" /> {live?.facing_mode === "environment" ? "Back camera" : "Front camera"}
                                    </button>
                                    <button
                                        data-testid="ls-camera-on"
                                        onClick={turnOnCamera}
                                        disabled={!useWp || savingField === "wp"}
                                        className="btn-ghost flex items-center justify-center gap-2 py-2 px-3 rounded-sm text-xs disabled:opacity-40"
                                    >
                                        <Camera size={14} weight="bold" /> On
                                    </button>
                                </div>
                            </div>

                            {/* Wallpaper */}
                            <div>
                                <div className="text-xs tracking-widest text-wta-muted uppercase mb-2 flex items-center gap-1">
                                    <ImageIcon size={12} /> Wallpaper {useWp && <span className="text-wta-primary">(camera off)</span>}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {PRESET_WALLPAPERS.map((w) => (
                                        <button
                                            key={w.id}
                                            type="button"
                                            data-testid={`ls-wp-${w.id}`}
                                            onClick={() => applyWallpaper(w.url)}
                                            className={`aspect-video rounded-sm overflow-hidden border-2 ${wpUrl === w.url && useWp ? "border-wta-primary" : "border-wta-border"}`}
                                        >
                                            <img src={w.url} alt={w.label} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <input
                                        data-testid="ls-wp-url"
                                        value={wpUrl}
                                        onChange={(e) => setWpUrl(e.target.value)}
                                        onBlur={() => wpUrl && applyWallpaper(wpUrl)}
                                        placeholder="Custom image URL..."
                                        className="flex-1 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-xs"
                                    />
                                    <label className="btn-ghost px-3 py-2 rounded-sm text-xs flex items-center gap-1 cursor-pointer shrink-0" title="Upload">
                                        <UploadSimple size={12} /> {uploadingWp ? "…" : "Upload"}
                                        <input data-testid="ls-wp-upload" type="file" accept="image/*" onChange={onUploadWp} className="hidden" />
                                    </label>
                                </div>
                            </div>
                        </div>
                    ) : tab === "mods" ? (
                        <div className="p-4 space-y-4">
                            <div>
                                <div className="text-xs tracking-widest text-wta-muted uppercase mb-2 flex items-center gap-1">
                                    <ShieldCheck size={12} /> Add Moderator
                                </div>
                                <div className="relative">
                                    <input
                                        data-testid="mod-search"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search by name…"
                                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm pl-8 pr-3 py-2 text-sm"
                                    />
                                    <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-wta-muted" />
                                </div>
                                {searching && <div className="text-xs text-wta-muted mt-1">Searching…</div>}
                                {searchResults.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        {searchResults.map((u) => (
                                            <button
                                                key={u.id}
                                                type="button"
                                                data-testid={`mod-add-${u.id}`}
                                                onClick={() => addMod(u)}
                                                className="w-full flex items-center gap-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-left hover:border-wta-primary"
                                            >
                                                <div className="w-7 h-7 rounded-full bg-black/40 overflow-hidden flex items-center justify-center text-xs font-heading">
                                                    {u.profile_pic ? <img src={u.profile_pic} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                                                </div>
                                                <div className="text-sm truncate">@{u.name}</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="text-xs tracking-widest text-wta-muted uppercase mb-2">Active Moderators ({mods.length})</div>
                                <div className="text-[10px] text-wta-muted mb-2">Persists across all your future live streams until you remove them.</div>
                                {mods.length === 0 ? (
                                    <div className="text-xs text-wta-muted">No moderators yet.</div>
                                ) : (
                                    <div className="space-y-1">
                                        {mods.map((m) => (
                                            <div key={m.user.id} className="flex items-center gap-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2">
                                                <div className="w-7 h-7 rounded-full bg-black/40 overflow-hidden flex items-center justify-center text-xs font-heading shrink-0">
                                                    {m.user.profile_pic ? <img src={m.user.profile_pic} alt="" className="w-full h-full object-cover" /> : m.user.name?.[0]?.toUpperCase()}
                                                </div>
                                                <div className="text-sm flex-1 truncate">@{m.user.name}</div>
                                                <button
                                                    data-testid={`mod-remove-${m.user.id}`}
                                                    onClick={() => removeMod(m.user.id)}
                                                    className="text-wta-muted hover:text-wta-primary p-1"
                                                    aria-label="Revoke"
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Link
                                to="/live-settings"
                                data-testid="ls-open-full-settings"
                                onClick={onClose}
                                className="block text-center text-xs font-heading uppercase tracking-widest text-wta-primary hover:underline pt-2"
                            >
                                Open full Live Settings →
                            </Link>
                        </div>
                    ) : (
                        // ===== Guests tab =====
                        <div className="p-4 space-y-4" data-testid="guests-panel">
                            <div className="bg-wta-surface2 border border-wta-border rounded-sm p-3 flex items-center gap-2">
                                <UsersThree size={18} weight="fill" className="text-wta-primary" />
                                <div className="text-xs flex-1">
                                    <div className="font-heading tracking-wider uppercase">Stage roster</div>
                                    <div className="text-wta-muted">{activeGuestsCount} of {GUEST_MAX} slot{GUEST_MAX === 1 ? "" : "s"} filled</div>
                                </div>
                            </div>

                            {/* Slot status */}
                            <div className="grid grid-cols-2 gap-2" data-testid="guest-slots">
                                {Array.from({ length: GUEST_MAX }).map((_, i) => {
                                    const g = guestList[i];
                                    if (!g) {
                                        return (
                                            <div key={i} data-testid={`guest-slot-empty-${i}`} className="aspect-square rounded-sm border border-dashed border-wta-border flex flex-col items-center justify-center text-[10px] tracking-widest uppercase text-wta-muted">
                                                <UserPlus size={20} />
                                                <span className="mt-1">Slot {i + 1}</span>
                                            </div>
                                        );
                                    }
                                    const isOnStage = g.status === "on_stage";
                                    return (
                                        <button
                                            key={g.user_id}
                                            type="button"
                                            data-testid={`guest-slot-${i}`}
                                            onClick={() => removeGuest(g.user_id, isOnStage)}
                                            className={`relative aspect-square rounded-sm overflow-hidden border ${isOnStage ? "border-wta-primary" : "border-yellow-500/50"} group`}
                                            title="Tap to remove"
                                        >
                                            <div className="w-full h-full bg-black/40 flex items-center justify-center font-heading text-2xl">
                                                {g.user?.profile_pic ? <img src={g.user.profile_pic} alt="" className="w-full h-full object-cover" /> : (g.user?.name?.[0]?.toUpperCase() || "?")}
                                            </div>
                                            <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-black/70 text-white text-[10px] truncate text-center">
                                                @{g.user?.name}
                                            </div>
                                            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-sm text-[9px] tracking-wider uppercase font-heading bg-black/70">
                                                {isOnStage ? <span className="text-wta-primary">on stage</span> : <span className="text-yellow-400">invited</span>}
                                            </div>
                                            <div className="absolute inset-0 bg-red-500/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-heading uppercase tracking-wider">
                                                <Trash size={14} weight="bold" /> &nbsp;Kick
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Invite search */}
                            <div>
                                <div className="text-xs tracking-widest text-wta-muted uppercase mb-2 flex items-center gap-1">
                                    <UserPlus size={12} weight="bold" /> Invite Guest
                                </div>
                                {activeGuestsCount >= GUEST_MAX ? (
                                    <div className="text-[11px] text-yellow-400">Stage is full. Remove a guest to invite another.</div>
                                ) : (
                                    <>
                                        <div className="relative">
                                            <input
                                                data-testid="guest-search"
                                                value={guestSearch}
                                                onChange={(e) => setGuestSearch(e.target.value)}
                                                placeholder="Search users to invite…"
                                                className="w-full bg-wta-surface2 border border-wta-border rounded-sm pl-8 pr-3 py-2 text-sm"
                                            />
                                            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-wta-muted" />
                                        </div>
                                        {guestSearching && <div className="text-xs text-wta-muted mt-1">Searching…</div>}
                                        {guestResults.length > 0 && (
                                            <div className="mt-2 space-y-1">
                                                {guestResults.map((u) => {
                                                    const already = guestList.some((g) => g.user_id === u.id);
                                                    return (
                                                        <button
                                                            key={u.id}
                                                            type="button"
                                                            data-testid={`guest-invite-${u.id}`}
                                                            disabled={already || invitingId === u.id}
                                                            onClick={() => inviteGuest(u)}
                                                            className="w-full flex items-center gap-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-left hover:border-wta-primary disabled:opacity-50"
                                                        >
                                                            <div className="w-7 h-7 rounded-full bg-black/40 overflow-hidden flex items-center justify-center text-xs font-heading">
                                                                {u.profile_pic ? <img src={u.profile_pic} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                                                            </div>
                                                            <div className="text-sm truncate flex-1">@{u.name}</div>
                                                            <span className="text-[10px] uppercase tracking-wider text-wta-primary">
                                                                {already ? "On list" : invitingId === u.id ? "…" : "Invite"}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}
                                <div className="text-[10px] text-wta-muted mt-2">Pending invites auto-expire after 5 minutes.</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* End live - always at bottom */}
                <div className="p-4 pb-16 md:pb-20 border-t border-wta-border">
                    <button
                        data-testid="ls-end-live"
                        onClick={onEnd}
                        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-sm text-sm font-heading uppercase tracking-wider bg-red-600/20 border border-red-500 text-red-300 hover:bg-red-600/30"
                    >
                        <Stop size={16} weight="fill" /> End Live
                    </button>
                </div>
            </div>
        </div>
    );
}
